// apick 1원 검증 3화면(verifying/verify_confirmed/verify_failed) — dumb 컴포넌트(props만, state 없음).
// OnboardingWizard 400 LOC 트립와이어 회피(CLAUDE.md Anti-Bloat). 카운트다운/StatusScreen 패턴 재사용.
import { useState } from 'react';
import { Check, AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusScreen } from './OnboardingStatusScreen';
import { GATEWAY_NUMBER_DISPLAY } from './wooriGuide';

// 진행바 분모. 서버 SQL DEADLINE_SEC(sent → 1원 SMS 대기 창)을 미러. 서버가 바꾸면 여기도 맞춰야 함.
const VERIFY_WINDOW_SEC = 60;

type Props = {
  phase: 'verifying' | 'verify_confirmed' | 'verify_failed';
  verifyRemainingSec: number | null;
  onRetry: () => void;       // 다시 검증(complete 재호출)
  onClose: () => void;       // 확인 대기로 두기 / 확인
  bankCode: string;
};

export function OnboardingVerifyScreens({ phase, verifyRemainingSec, onRetry, onClose, bankCode }: Props) {
  // hook 규칙: 아래 조건부 early-return들보다 앞에 선언(verify_failed 전용이지만 항상 호출됨).
  const [reack, setReack] = useState(false);
  if (phase === 'verifying') {
    // verifyRemainingSec === null = 아직 'sending'(deadline_at 미정) — 1원 송금 결과 확인 중.
    // 카운트다운/진행바 없이 indeterminate. deadline 생기면(sent) 카운트다운 표시.
    const counting = verifyRemainingSec != null;
    const pct = counting
      ? Math.max(0, Math.min(100, (verifyRemainingSec / VERIFY_WINDOW_SEC) * 100)) : 0;
    const low = counting && verifyRemainingSec <= 15;
    return (
      <div className="flex flex-col items-center px-1 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 ring-1 ring-teal-100">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
        <h3 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">
          {counting ? '1원을 보냈어요 💸' : '1원 송금 중…'}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {counting ? '입금 통지가 도착하는지 확인 중이에요 (최대 60초)' : '송금 결과를 확인하고 있어요'}
        </p>
        {counting && (
          <>
            <div className="mx-auto mt-5 h-1 w-full max-w-[13rem] overflow-hidden rounded-full bg-slate-200/70">
              <div className={cn('h-full rounded-full transition-[width] duration-1000 ease-linear',
                                 low ? 'bg-amber-500' : 'bg-teal-500')}
                   style={{ width: `${pct}%` }} />
            </div>
            <p className={cn('mt-2 text-xs tabular-nums', low ? 'text-amber-600' : 'text-slate-400')}>
              {verifyRemainingSec}초 남음
            </p>
          </>
        )}
      </div>
    );
  }
  if (phase === 'verify_confirmed') {
    return (
      <StatusScreen tone="teal" icon={<Check className="h-7 w-7" />} title="검증 완료 ✅"
        footer={<button onClick={onClose}
          className="w-full rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white">확인</button>}>
        <p className="mt-2 text-sm text-slate-500">
          이제 이 계좌로 들어온 입금이 자동으로 주문과 매칭돼요.
        </p>
      </StatusScreen>
    );
  }
  // verify_failed — expired/expired_late/failed/send_unknown 통합. 가장 흔한 원인(통지옵션)을
  // 안내하되 송금 자체 실패 가능성도 함께 언급(특정 원인 단정 회피).
  const isHana = bankCode === 'Hana';
  const isShinhan = bankCode === 'Shinhan';
  const isNonghyup = bankCode === 'Nonghyup';
  const isKB = bankCode === 'KB';
  const isIBK = bankCode === 'IBK';
  return (
    <StatusScreen tone="amber" icon={<AlertTriangle className="h-7 w-7" />} title="검증을 마치지 못했어요"
      footer={
        <div className="w-full">
          {(isHana || isKB) && (
            <label className="mb-2 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={reack} onChange={(e) => setReack(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-teal-600" />
              <span>설정을 고쳤어요. 다시 1원을 보내 확인할게요.</span>
            </label>
          )}
          <div className="flex w-full gap-2">
            <button onClick={onClose}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600">
              확인 대기로 두기
            </button>
            <button onClick={onRetry} disabled={(isHana || isKB) && !reack}
              className="flex-1 rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              다시 검증
            </button>
          </div>
        </div>
      }>
      <p className="mt-2 text-sm text-slate-500">
        1원 입금 통지를 받지 못했어요. 대부분 통지옵션 때문이에요.
      </p>
      <div className="mt-3 w-full rounded-xl bg-amber-50/60 px-4 py-3 text-left text-sm text-amber-800 ring-1 ring-amber-100">
        <p className="font-semibold">{isHana ? '하나원큐 설정을 확인해주세요' : isShinhan ? '신한 SOL뱅크 통지옵션을 확인해주세요' : isNonghyup ? '농협은행 앱 알림설정을 확인해주세요' : isKB ? 'KB스타뱅킹 알림설정을 확인해주세요' : isIBK ? '기업은행 앱 알림설정을 확인해주세요' : '우리은행 통지옵션을 확인해주세요'}</p>
        {isHana ? (
          <>
            <p className="mt-1">① 알림금액 <b>"전액"</b> (1원까지)</p>
            <p>② 휴대폰번호에 <b>{GATEWAY_NUMBER_DISPLAY}</b> 입력 후 <b>[휴대폰번호 추가]</b>까지 눌렀는지</p>
          </>
        ) : isNonghyup ? (
          <>
            <p className="mt-1">① 금액제한 <b>"제한없음"</b> (1원까지)</p>
            <p>② 알림받을 휴대전화에 <b>{GATEWAY_NUMBER_DISPLAY}</b> 입력 후 <b>[휴대전화 추가]</b>까지 눌렀는지</p>
          </>
        ) : isShinhan ? (
          <>
            <p className="mt-1">① 통지항목 <b>"입금거래"</b> 또는 <b>"모든거래"</b></p>
            <p>② 계좌번호표시여부 <b>"표시함"</b></p>
          </>
        ) : isKB ? (
          <>
            <p className="mt-1">① 통지금액 <b>"전액"</b> (1원까지)</p>
            <p>② 휴대폰번호에 <b>{GATEWAY_NUMBER_DISPLAY}</b> 입력 후 <b>[휴대폰번호 추가]</b>까지 눌렀는지</p>
          </>
        ) : isIBK ? (
          <>
            <p className="mt-1">① 계좌번호표시구분 <b>"일반계좌번호"</b></p>
            <p>② 알림 휴대폰번호에 <b>{GATEWAY_NUMBER_DISPLAY}</b> 입력 후 <b>[인증하기]</b>를 눌렀는지</p>
          </>
        ) : (
          <>
            <p className="mt-1">① 알림 금액 <b>"모두"</b> (1원까지)</p>
            <p>② <b>입금 알림 포함</b> 체크</p>
          </>
        )}
      </div>
    </StatusScreen>
  );
}
